import { Alert } from "antd";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import cn from "classnames";
import { capitalize } from "lodash";

import { useAssessmentInsights } from "@/modules/assessments/hooks/use-assessment-insights.hook";
import { CompetencyInsights } from "@/modules/assessments/types/insights.interface";
import tableStyle from "@/modules/tables/style.module.css";

import "../../styles/assessmentCommon.css";
import { DateRangeFilter } from "./components/DateRangeFilter";
import { InsightsSkeleton } from "./components/InsightsSkeleton";
import { InsightsSummaryRow } from "./components/InsightsSummaryRow";
import { ScoreDistributionChart } from "./components/ScoreDistributionChart";
import { SectionInfoButton } from "./components/SectionInfoButton";
import { SectionInfoModal, SectionInfoVariant } from "./components/SectionInfoModal";
import chartStyle from "./components/score-distribution-chart.module.css";

function InsightsLayout({ empty, children }: { empty?: boolean; children: React.ReactNode }) {
  return (
    <div className="container">
      <div className="tabs-inner-container-desktop">
        <div className={cn(tableStyle.tableBlock, { [tableStyle["tableBlock--empty"]]: empty })}>{children}</div>
      </div>
    </div>
  );
}

function CompetencyChartItem({ competency }: { competency: CompetencyInsights }) {
  if (!competency.subCompetencies || competency.subCompetencies.length === 0) {
    return (
      <ScoreDistributionChart
        icon={competency.icon}
        title={competency.compositeTitle}
        distribution={competency.distribution}
      />
    );
  }

  return (
    <div className={chartStyle.competencyGroup}>
      <ScoreDistributionChart
        icon={competency.icon}
        title={competency.compositeTitle}
        distribution={competency.distribution}
        className={chartStyle.parentChartCard}
      />
      <div className={chartStyle.subCompetencyContainer}>
        {competency.subCompetencies.map((sub) => (
          <ScoreDistributionChart
            key={sub.compositeId}
            icon={sub.icon}
            title={sub.compositeTitle}
            distribution={sub.distribution}
          />
        ))}
      </div>
    </div>
  );
}

const strengthVariantStyle = {
  weak: chartStyle.pillWeak,
  average: chartStyle.pillAverage,
  strong: chartStyle.pillStrong,
} as const;

type StrengthVariant = keyof typeof strengthVariantStyle;

function StrengthPill({ variant }: { variant: StrengthVariant }) {
  return <span className={cn(chartStyle.strengthPill, strengthVariantStyle[variant])}>{capitalize(variant)}</span>;
}

const DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_DAYS_BACK = 90;

function parseMoment(value: string | null): moment.Moment | null {
  if (!value) return null;
  const parsed = moment(value, DATE_FORMAT, true);
  return parsed.isValid() ? parsed : null;
}

export function AssessmentInsights() {
  const { assessment_id } = useParams<{ assessment_id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultStartStr = useMemo(() => moment().subtract(DEFAULT_DAYS_BACK, "days").format(DATE_FORMAT), []);
  const defaultEndStr = useMemo(() => moment().format(DATE_FORMAT), []);

  // URL param convention: absent = use default, empty string = cleared, value = specific date
  const urlStart = searchParams.get("startDate");
  const urlEnd = searchParams.get("endDate");

  const pickerStart = useMemo<moment.Moment | null>(
    () => (urlStart === null ? moment().subtract(DEFAULT_DAYS_BACK, "days") : parseMoment(urlStart)),
    [urlStart],
  );
  const pickerEnd = useMemo<moment.Moment | null>(() => (urlEnd === null ? moment() : parseMoment(urlEnd)), [urlEnd]);

  const appliedStart = urlStart === null ? defaultStartStr : urlStart || undefined;
  const appliedEnd = urlEnd === null ? defaultEndStr : urlEnd || undefined;

  const updateDateParam = useCallback(
    (key: string, date: moment.Moment | null) => {
      const params = new URLSearchParams(window.location.search);
      if (date?.isValid()) params.set(key, date.format(DATE_FORMAT));
      else params.set(key, "");
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const [activeInfoModal, setActiveInfoModal] = useState<SectionInfoVariant | null>(null);
  const { data, isLoading, error } = useAssessmentInsights(Number(assessment_id), appliedStart, appliedEnd);
  const hasValidationError = pickerStart != null && pickerEnd != null && pickerStart.isAfter(pickerEnd, "day");
  const validationError = hasValidationError ? "Start date cannot be after end date" : undefined;

  if (error) {
    return (
      <InsightsLayout empty={true}>
        <Alert
          type="error"
          showIcon
          message="Unable to load insights data"
          description="Something went wrong while loading the insights. Please try again later."
        />
      </InsightsLayout>
    );
  }

  return (
    <InsightsLayout>
      <div className={chartStyle.insightsHeader}>
        <h1 className={chartStyle.insightsHeading}>Candidate Insights</h1>
        <div className={chartStyle.strengthKey}>
          <span className={chartStyle.strengthKeyLabel}>Key:</span>
          <StrengthPill variant="weak" />
          <StrengthPill variant="average" />
          <StrengthPill variant="strong" />
        </div>
      </div>

      <div className={chartStyle.sectionHeadingRow}>
        <h2 className={chartStyle.sectionHeading}>Filters</h2>
        <SectionInfoButton onClick={() => setActiveInfoModal("filters")} ariaLabel="View filters information" />
      </div>
      <DateRangeFilter
        startDate={pickerStart}
        endDate={pickerEnd}
        onStartDateChange={(date) => updateDateParam("startDate", date)}
        onEndDateChange={(date) => updateDateParam("endDate", date)}
        validationError={validationError}
      />

      {isLoading ? (
        <InsightsSkeleton />
      ) : (
        data && (
          <>
            <div className={chartStyle.sectionHeadingRow}>
              <h2 className={chartStyle.sectionHeading}>Completion Metrics</h2>
              <SectionInfoButton
                onClick={() => setActiveInfoModal("completionMetrics")}
                ariaLabel="View completion metrics information"
              />
            </div>
            <InsightsSummaryRow summary={data.summary} />

            <div className={chartStyle.sectionHeadingRow}>
              <h2 className={chartStyle.sectionHeading}>Candidate Results</h2>
              <SectionInfoButton
                onClick={() => setActiveInfoModal("candidateResults")}
                ariaLabel="View candidate results information"
              />
            </div>
            <div className={chartStyle.competencyGrid}>
              {data.competencies.map((competency) => (
                <CompetencyChartItem key={competency.compositeId} competency={competency} />
              ))}
            </div>
          </>
        )
      )}

      <SectionInfoModal variant={activeInfoModal} onClose={() => setActiveInfoModal(null)} />
    </InsightsLayout>
  );
}
